import React, { createContext, useContext, useRef, ReactNode } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface DataCacheContextType {
  getCache: <T>(key: string) => T | null;
  setCache: <T>(key: string, data: T, duration?: number) => void;
  clearCache: (key?: string) => void;
}

const DataCacheContext = createContext<DataCacheContextType | null>(null);

const DEFAULT_CACHE_DURATION = 300000; // 5 minutes

export function DataCacheProvider({ children }: { children: ReactNode }) {
  const cacheRef = useRef<Map<string, CacheEntry<any>>>(new Map());

  const getCache = <T,>(key: string): T | null => {
    const entry = cacheRef.current.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > DEFAULT_CACHE_DURATION) {
      // Cache expired
      cacheRef.current.delete(key);
      return null;
    }

    return entry.data as T;
  };

  const setCache = <T,>(key: string, data: T, duration = DEFAULT_CACHE_DURATION) => {
    cacheRef.current.set(key, {
      data,
      timestamp: Date.now()
    });
  };

  const clearCache = (key?: string) => {
    if (key) {
      cacheRef.current.delete(key);
    } else {
      cacheRef.current.clear();
    }
  };

  return (
    <DataCacheContext.Provider value={{ getCache, setCache, clearCache }}>
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
}